import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ScrollText, Clapperboard } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LangContext";
import { Button } from "../../components/ui/button";
import DubImport from "./dubbing/DubImport";
import DubJobCard, { ACTIVE } from "./dubbing/DubJobCard";
import DubStartDialog from "./dubbing/DubStartDialog";
import DubAudit from "./dubbing/DubAudit";

export default function DubbingTab() {
  const { isAdmin } = useAuth();
  const { t } = useLang();
  const [cfg, setCfg] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [starting, setStarting] = useState(null);
  const [view, setView] = useState("jobs");

  const load = useCallback(() => api.get("/dub/jobs").then((r) => setJobs(r.data)).catch(() => {}), []);
  useEffect(() => { api.get("/dub/config").then((r) => setCfg(r.data)).catch(() => {}); load(); }, [load]);
  const anyActive = jobs.some((j) => ACTIVE.includes(j.status));
  useEffect(() => { if (!anyActive) return undefined; const id = setInterval(load, 4000); return () => clearInterval(id); }, [anyActive, load]);

  const resume = async (job) => {
    try { await api.post(`/dub/jobs/${job.id}/resume`); toast.success(t("A retomar…", "Resuming…")); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (job) => {
    if (!window.confirm(t("Apagar este vídeo e os ficheiros gerados (também a pasta no My Cloud)? O vídeo original importado da pasta Blog não é apagado.", "Delete this video and its generated files (including its My Cloud folder)? Originals imported from the Blog folder are kept."))) return;
    try { await api.delete(`/dub/jobs/${job.id}`); toast.success(t("Removido", "Removed")); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="dubbing-tab" className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading text-2xl font-bold mr-auto flex items-center gap-2"><Clapperboard className="h-6 w-6 text-primary" />{t("Tradução e dobragem de vídeos", "Video translation & dubbing")}</h2>
        {isAdmin && (
          <Button data-testid="dub-toggle-audit-btn" size="sm" variant="secondary" className="gap-1.5" onClick={() => setView(view === "jobs" ? "audit" : "jobs")}>
            <ScrollText className="h-4 w-4" />{view === "jobs" ? t("Auditoria de direitos", "Rights audit") : t("Voltar aos vídeos", "Back to videos")}
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground max-w-3xl">{t("Localize vídeos próprios ou autorizados (cursos, formação, marketing, conteúdos corporativos ou educacionais) para novos idiomas: transcrição, tradução e dobragem automática, com legendas.", "Localise your own or authorised videos (courses, training, marketing, corporate or educational content) into new languages: transcription, translation and automatic dubbing, with subtitles.")}</p>
      {view === "audit" ? <DubAudit /> : (
        <>
          <DubImport cfg={cfg} onCreated={load} />
          <div className="grid gap-3" data-testid="dub-jobs-list">
            {jobs.length === 0 && <p className="text-muted-foreground text-sm" data-testid="dub-jobs-empty">{t("Ainda não importou nenhum vídeo.", "No videos imported yet.")}</p>}
            {jobs.map((j) => <DubJobCard key={j.id} job={j} onStart={setStarting} onDelete={remove} onResume={resume} />)}
          </div>
        </>
      )}
      {starting && <DubStartDialog job={starting} cfg={cfg} onClose={() => setStarting(null)} onStarted={() => { setStarting(null); load(); }} />}
    </div>
  );
}
